import React, { useContext, useEffect, useState, useRef } from "react";
import { FaTimes, FaPowerOff } from "react-icons/fa";
import AuthContext from "../../context/AuthContext.js";
import api from "../../services/AxiosConnection.js";
import { useNotification } from "../../context/NotificationContext.js";
import NotificationSnackbar from "../../utils/NotificationSnackbar.js";
import TrainDetails from "./TrainDetails.js";
import TempLogTable from "./TempLogTable.js";
import AlarmsAndWarnings from "./AlarmsAndWarnings.js";
import TrainLogList from "./TrainLogList.js";
import { Vortex } from "react-loader-spinner";
import AlarmsDialog from "./AlarmsDialog.js";
import { useLocation } from "react-router-dom";
import WarningsDialog from "./WarningsDialog.js";
import socket from "../../services/socket.js";

function SimpleView() {
  const location = useLocation();
  const { siteDb: routeSiteDb, trainId: routeTrainId } = location.state || {};
  const { selectedSite, setSelectedSite, fromDate, setFromDate, toDate, setToDate } = useContext(AuthContext);
  const [trainLogList, setTrainLogList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [trainDetails, setTrainDetails] = useState(null);
  const [temperatureLogs, setTemperatureLogs] = useState([]);
  const [alarms, setAlarms] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const { notification, closeNotification } = useNotification();
  const { triggerNotification } = useNotification();
  const filteredAlarms = alarms.filter((alarm) => !alarm.Acknowledged);
  const filteredWarnings = warnings.filter((warning) => !warning.Acknowledged);
  const [highlightedTrainId, setHighlightedTrainId] = useState(null);
  const highlightedTrainIdRef = useRef(highlightedTrainId);
  useEffect(() => {
    highlightedTrainIdRef.current = highlightedTrainId;
  }, [highlightedTrainId]);
  
  // States for sessions popup
  const [showSessionsPopup, setShowSessionsPopup] = useState(false);
  const [activeSessions, setActiveSessions] = useState([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [loggingOutSession, setLoggingOutSession] = useState(null);
  
  const rightColumnRef = useRef(null);
  const [rightColumnHeight, setRightColumnHeight] = useState(0);
  
  // Ref for click outside detection
  const modalRef = useRef(null);
  
  // Check for sessions popup flag on component mount
  useEffect(() => {
    const showPopup = sessionStorage.getItem("showSessionsPopup");
    const userId = sessionStorage.getItem("userId");
    
    if (showPopup === "true" && userId) {
      fetchActiveSessions(userId);
      // Clear the flags so popup doesn't show again
      sessionStorage.removeItem("showSessionsPopup");
      sessionStorage.removeItem("userId");
    }
  }, []);
  
  // Prevent body scroll when popup is open
  useEffect(() => {
    if (showSessionsPopup) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showSessionsPopup]);
  
  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        closeSessionsPopup();
      }
    };
    
    if (showSessionsPopup) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSessionsPopup]);
  
  const fetchActiveSessions = async (userId) => {
    setIsLoadingSessions(true);
    try {
      const response = await api.post("/users/getActiveSessions", { userId });
      if (response.data?.success) {
        setActiveSessions(response.data.sessions);
        setShowSessionsPopup(true);
      }
    } catch (error) {
      console.error("Failed to fetch active sessions:", error);
      triggerNotification("Could not retrieve active sessions", "error");
    } finally {
      setIsLoadingSessions(false);
    }
  };
  
  const logoutSession = async (sessionId) => {
    setLoggingOutSession(sessionId);
    try {
      const response = await api.put("/users/logoutSessionById", { sessionId });
      if (response.data?.success) {
        triggerNotification("Session logged out successfully", "success");
        // Fetch updated sessions
        const userId = activeSessions[0]?.UserID;
        if (userId) {
          await fetchActiveSessions(userId);
        }
      }
    } catch (error) {
      console.error("Failed to logout session:", error);
      triggerNotification("Could not logout session", "error");
    } finally {
      setLoggingOutSession(null);
    }
  };
  
  const closeSessionsPopup = () => {
    setShowSessionsPopup(false);
    setActiveSessions([]);
  };
  
  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const dateParts = dateString.split('T')[0]?.split('-');
    const timeParts = dateString.split('T')[1]?.split('.')[0]?.split(':');
    if (!dateParts || !timeParts) return dateString;
    
    const year = dateParts[0];
    const month = dateParts[1];
    const day = dateParts[2];
    
    let hours = parseInt(timeParts[0], 10);
    const minutes = timeParts[1];
    
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    
    return `${day}/${month}/${year} ${hours}:${minutes} ${ampm}`;
  };
  
  // Effect to update the height of the right column dynamically
  useEffect(() => {
    const updateHeight = () => {
      if (rightColumnRef.current) {
        const height = rightColumnRef.current.offsetHeight;
        setRightColumnHeight(height);
      }
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => {
      window.removeEventListener("resize", updateHeight);
    };
  }, [trainDetails, temperatureLogs, alarms, warnings]);

  // Sync site from route state if provided
  useEffect(() => {
    if (routeSiteDb && routeSiteDb !== selectedSite) {
      setSelectedSite(routeSiteDb);
    }
  }, [routeSiteDb, selectedSite, setSelectedSite]);

  // Reusable helper to fetch a specific train's details, temperature logs, and alerts
  const fetchTrainDetailsData = async (trainId, site) => {
    const currentSite = site || selectedSite;
    if (!trainId || !currentSite) return;
    setHighlightedTrainId(trainId);

    try {
      const response = await api.get("/simple-view/getTrainDetailsByTrainId", {
        params: { siteDb: currentSite, trainId },
      });
      if (response.data?.trainDetails?.length > 0) {
        setTrainDetails(response.data.trainDetails[0]);
      } else {
        setTrainDetails(null);
      }
    } catch (error) {
      console.error("Error fetching train details", error);
      setTrainDetails(null);
    }

    try {
      const temperatureLogResponse = await api.get(
        "/simple-view/getTemperatureLogByTrainId",
        {
          params: { siteDb: currentSite, trainId },
        }
      );
      if (temperatureLogResponse.data?.temperatureLogs?.length > 0) {
        setTemperatureLogs(temperatureLogResponse.data.temperatureLogs);
      } else {
        setTemperatureLogs([]);
      }
    } catch (error) {
      console.error("Error fetching temperature logs", error);
      setTemperatureLogs([]);
    }

    try {
      const alarmAndWarningResponse = await api.get(
        "/simple-view/getAlertsByTrainId",
        {
          params: { siteDb: currentSite, trainId },
        }
      );
      setAlarms(alarmAndWarningResponse.data?.alarms || []);
      setWarnings(alarmAndWarningResponse.data?.warnings || []);
    } catch (error) {
      console.error("Error fetching alerts", error);
      setAlarms([]);
      setWarnings([]);
    }
  };

  // Primary unified effect to load train logs and the initial train details in a single loading cycle
  useEffect(() => {
    if (!selectedSite) return;
    let isMounted = true;

    const loadInitialData = async () => {
      setLoading(true);
      try {
        let response;
        if (fromDate && toDate) {
          response = await api.get("/simple-view/getTrainIdsByDateRange", {
            params: { siteDb: selectedSite, fromDate, toDate },
          });
        } else {
          response = await api.get("/simple-view/getAllTrainIds", {
            params: { siteDb: selectedSite },
          });
        }

        const trains = response.data?.trainIds || [];
        if (isMounted) {
          setTrainLogList(trains);
        }

        if (trains.length > 0) {
          const targetTrainId = routeTrainId && trains.some((t) => t.TrainId === routeTrainId)
            ? routeTrainId
            : trains[0]?.TrainId;

          if (isMounted && targetTrainId) {
            await fetchTrainDetailsData(targetTrainId, selectedSite);
          }
        } else if (isMounted) {
          setTrainDetails(null);
          setTemperatureLogs([]);
          setAlarms([]);
          setWarnings([]);
          setHighlightedTrainId(null);
        }
      } catch (error) {
        console.error("Error loading initial data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite, routeSiteDb, routeTrainId]);

  // Socket.IO real-time listener & 5-minute interval polling without disruptive page reload or full-screen loading
  useEffect(() => {
    if (!selectedSite) return;

    if (socket.connected) {
      socket.emit("join_site", selectedSite);
    }

    const handleConnect = () => {
      socket.emit("join_site", selectedSite);
    };

    const fetchLatestTrainData = async (isSocketEvent = false) => {
      try {
        let response;
        if (fromDate && toDate) {
          response = await api.get("/simple-view/getTrainIdsByDateRange", {
            params: { siteDb: selectedSite, fromDate, toDate },
          });
        } else {
          response = await api.get("/simple-view/getAllTrainIds", {
            params: { siteDb: selectedSite },
          });
        }
        const newTrainIds = response.data?.trainIds;
        if (Array.isArray(newTrainIds) && newTrainIds.length > 0) {
          setTrainLogList((prev) => {
            const hasNewTrain = prev.length > 0 && prev[0]?.TrainId !== newTrainIds[0]?.TrainId;
            const countChanged = prev.length !== newTrainIds.length;

            if (hasNewTrain) {
              const latestTrainId = newTrainIds[0].TrainId;
              // If user is watching live (no custom date range selected)
              if (!fromDate && !toDate) {
                // If user was on the top train or no train selected yet, automatically load new train details
                if (!highlightedTrainIdRef.current || highlightedTrainIdRef.current === prev[0]?.TrainId) {
                  fetchTrainDetailsData(latestTrainId, selectedSite);
                }
                triggerNotification(`New train detected: ${latestTrainId}`, "info");
              } else {
                triggerNotification("New train log received from site", "info");
              }
              return newTrainIds;
            } else if (countChanged || prev.length === 0) {
              return newTrainIds;
            }
            return prev;
          });
        }
      } catch (err) {
        console.error("Error refreshing train logs:", err);
      }
    };

    const handleDataUpdated = (updatedData) => {
      if (updatedData && updatedData.siteDb && updatedData.siteDb !== selectedSite) return;
      fetchLatestTrainData(true);
    };

    socket.on("connect", handleConnect);
    socket.on("division_data_updated", handleDataUpdated);

    // 5 minutes fallback polling interval
    const FIVE_MINUTES_MS = 5 * 60 * 1000;
    const intervalId = setInterval(() => {
      fetchLatestTrainData(false);
    }, FIVE_MINUTES_MS);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("division_data_updated", handleDataUpdated);
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite, fromDate, toDate]);
  
  const handleTrainLogClick = async (trainId) => {
    if (!trainId || !selectedSite) return;
    setLoading(true);
    try {
      await fetchTrainDetailsData(trainId, selectedSite);
      triggerNotification("Train data fetched successfully!", "success");
    } catch (error) {
      console.error("Error fetching train details on click:", error);
      triggerNotification("No data found for the selected train!", "error");
    } finally {
      setLoading(false);
    }
  };
  
  const handleCheckLiveTrainStatus = async () => {
    if (!selectedSite) {
      triggerNotification("No site selected", "error");
      return;
    }
    setLoading(true);
    setFromDate(""); // Reset fromDate in context
    setToDate(""); // Reset toDate in context
    try {
      const response = await api.get("/simple-view/getAllTrainIds", {
        params: { siteDb: selectedSite },
      });
      const trains = response.data?.trainIds || [];
      setTrainLogList(trains);
      if (trains.length > 0) {
        triggerNotification("Live train data fetched successfully", "success");
        await fetchTrainDetailsData(trains[0].TrainId, selectedSite);
      } else {
        triggerNotification("No live train data available for this site", "warning");
        setTrainDetails(null);
        setTemperatureLogs([]);
        setAlarms([]);
        setWarnings([]);
        setHighlightedTrainId(null);
      }
    } catch (error) {
      console.error("Error fetching live train logs", error);
      triggerNotification("Error fetching live train data", "error");
      setTrainLogList([]);
    } finally {
      setLoading(false);
    }
  };
  
  const handleFilter = async () => {
    const validateDates = () => {
      if (!fromDate || !toDate) {
        triggerNotification("Please select both from and to dates.", "error");
        return false;
      }
      const fromDateObj = new Date(fromDate);
      const toDateObj = new Date(toDate);
      fromDateObj.setHours(0, 0, 0, 0);
      toDateObj.setHours(0, 0, 0, 0);
      const twoMonthsInMs = 60 * 24 * 60 * 60 * 1000;
      if (toDateObj - fromDateObj > twoMonthsInMs) {
        triggerNotification("Date range cannot exceed 2 months.", "error");
        return false;
      }
      if (fromDateObj > toDateObj) {
        triggerNotification("From date should be earlier than to date.", "error");
        return false;
      }
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (toDateObj > today) {
        triggerNotification("To date should not be greater than today's date.", "error");
        return false;
      }
      return true;
    };
    if (!validateDates()) return;
    setLoading(true);
    try {
      const response = await api.get("/simple-view/getTrainIdsByDateRange", {
        params: { siteDb: selectedSite, fromDate, toDate },
      });
      const trains = response.data?.trainIds || [];
      setTrainLogList(trains);
      if (trains.length > 0) {
        triggerNotification(
          `Trains From ${fromDate} to ${toDate} fetched Successfully`,
          "success"
        );
        await fetchTrainDetailsData(trains[0].TrainId, selectedSite);
      } else {
        triggerNotification(
          response.data?.message || `No train logs found from ${fromDate} to ${toDate}`,
          "warning"
        );
        setTrainDetails(null);
        setTemperatureLogs([]);
        setAlarms([]);
        setWarnings([]);
        setHighlightedTrainId(null);
      }
    } catch (error) {
      console.error("Error fetching train logs by date", error);
      triggerNotification("Error fetching train logs by date", "error");
      setTrainLogList([]);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchTrainLogs = async () => {
    setLoading(true);
    try {
      let response;
      if (fromDate && toDate) {
        response = await api.get("/simple-view/getTrainIdsByDateRange", {
          params: { siteDb: selectedSite, fromDate, toDate },
        });
      } else {
        response = await api.get("/simple-view/getAllTrainIds", {
          params: { siteDb: selectedSite },
        });
      }
      const trains = response.data?.trainIds || [];
      setTrainLogList(trains);
      if (trains.length > 0 && (!highlightedTrainId || !trains.some(t => t.TrainId === highlightedTrainId))) {
        await fetchTrainDetailsData(trains[0].TrainId, selectedSite);
      }
    } catch (error) {
      console.error("Error fetching train logs", error);
      setTrainLogList([]);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="bg-gray-100 min-h-screen flex flex-col">
      {loading && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-40">
          <Vortex
            visible={true}
            height={80}
            width={80}
            ariaLabel="vortex-loading"
            wrapperStyle={{}}
            wrapperClass="vortex-wrapper"
            colors={["#2563eb", "#1e40af", "#60a5fa", "#3b82f6", "#1d4ed8", "#93c5fd"]}
          />
        </div>
      )}
      
      {/* Active Sessions Popup */}
      {showSessionsPopup && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={closeSessionsPopup}
        >
          <div 
            ref={modalRef}
            className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col border border-gray-200/50 animate-in fade-in zoom-in duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 flex justify-between items-center rounded-t-2xl">
              <h3 className="text-xl font-bold flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Active Sessions
              </h3>
              <button 
                onClick={closeSessionsPopup}
                className="text-white hover:text-gray-200 p-1 rounded-full transition-all duration-200 hover:bg-white/20 hover:scale-110"
                aria-label="Close"
              >
                <FaTimes className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto flex-grow">
              {isLoadingSessions ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : activeSessions.length > 0 ? (
                <div className="space-y-4">
                  <p className="text-gray-700 text-sm">
                    You have <span className="font-semibold text-blue-600">{activeSessions.length}</span> active session{activeSessions.length !== 1 ? 's' : ''}:
                  </p>
                  
                  <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">IP Address</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Login Time</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {activeSessions.map((session) => (
                          <tr key={session.ID} className="hover:bg-gray-50 transition-all duration-150">
                            <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                              {session.IPAddress}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {formatDateTime(session.LoginTime)}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 text-xs rounded-full font-semibold ${session.IsActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                {session.IsActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <button
                                onClick={() => logoutSession(session.SessionId)}
                                disabled={loggingOutSession === session.SessionId}
                                className={`inline-flex items-center justify-center p-1.5 border-2 border-transparent text-sm font-medium rounded-lg transition-all duration-200 ${loggingOutSession === session.SessionId ? 'border-gray-300 text-gray-400 cursor-not-allowed' : 'border-red-500 text-red-500 hover:border-red-600 hover:text-red-600 hover:bg-red-50'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500`}
                                title="Logout Session"
                              >
                                {loggingOutSession === session.SessionId ? (
                                  <svg className="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                ) : (
                                  <FaPowerOff className="h-3.5 w-3.5" />
                                )}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-3">
                    <svg className="h-6 w-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-1">No Active Sessions</h3>
                  <p className="text-sm text-gray-500">You don't have any active sessions at this time.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        type={notification.type}
        onClose={closeNotification}
      />
      
      <div className="px-3 pt-2 pb-3 sm:px-4 sm:pt-2 sm:pb-4 flex-grow bg-gray-100 min-h-screen">
        <div className="flex flex-col lg:flex-row gap-4 h-full">
          {/* Left Column */}
          <div className="lg:w-1/4 flex flex-col h-full">
            {/* Train Log Selection */}
            <div className="bg-[#e6e7eb] p-4 rounded-xl mb-4 h-full flex flex-col shadow-sm">
              <h2 className="font-bold mb-4 text-black">Select Train Log</h2>
              <div className="space-y-4 flex-grow">
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="flex-1">
                    <label className="block text-xs mb-1 text-gray-700">From Date:</label>
                    <input
                      type="date"
                      className="w-full p-1 text-sm rounded border border-gray-300 bg-white"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs mb-1 text-gray-700">To Date:</label>
                    <input
                      type="date"
                      className="w-full p-1 text-sm rounded border border-gray-300 bg-white"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                    />
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    className="w-full sm:w-1/2 bg-blue-900 text-white py-1 text-sm rounded"
                    onClick={handleFilter}
                  >
                    Filter
                  </button>
                  <button
                    className="w-full sm:w-1/2 bg-blue-900 text-white py-1 text-sm rounded"
                    onClick={handleCheckLiveTrainStatus}
                  >
                    Live Train
                  </button>
                </div>
              </div>
            </div>
            {/* Train Log List - Takes remaining height */}
            <div className="flex-grow">
              <TrainLogList
                trainLogList={trainLogList}
                onLogClick={handleTrainLogClick}
                highlightedTrainId={highlightedTrainId}
                fetchTrainLogs={fetchTrainLogs}
                rightColumnHeight={rightColumnHeight}
                enableDoubleClickRedirect={true}
                fromDate={fromDate}
                toDate={toDate}
                selectedSite={selectedSite}
              />
            </div>
          </div>
          {/* Right Column */}
          <div
            ref={rightColumnRef}
            className="lg:w-3/4 w-full mx-auto flex flex-col"
          >
            {trainDetails && <TrainDetails trainDetails={trainDetails} />}
            <TempLogTable
              temperatureLogs={temperatureLogs}
              trainId={highlightedTrainId}
              alarms={alarms}
              warnings={warnings}
            />
            <AlarmsAndWarnings alarms={alarms} warnings={warnings} trainId={highlightedTrainId} />
          </div>
        </div>
      </div>
      <AlarmsDialog alarms={filteredAlarms} />
      <WarningsDialog warnings={filteredWarnings} />
    </div>
  );
}
export default SimpleView;